<template>
  <div v-if="hasAttributeList" class="sp-finder-result-attributes" :class="`--layout-${layout}`" part="base">
    <div class="sp-finder-result-attributes__list">
      <section v-if="showInsurancePlanStatus" class="sp-finder-result-attributes__detail">
        <sp-insurance-app
          class="sp-finder-result-attributes__insurance-app"
          variant="tiny"
          :status="insurancePlanStatus.status"
          :insurance-plan="insurancePlanStatus.insurancePlanName"
          :provider="insurancePlanStatus.providerName"
          :accepted-label="t('in_network')"
          static
        ></sp-insurance-app>
      </section>

      <sp-tile
        v-for="tile in attributeTiles"
        :key="tile.key"
        class="sp-finder-result-attributes__tile"
        :class="tile.className"
        :icon="tile.icon"
        layout="row"
        align="center"
        :size="tileSize"
        type="body"
        :href="tile.href"
        :label="tile.label"
      ></sp-tile>

      <sp-tile
        v-if="showLgbtqiaAffirmingCare"
        class="sp-finder-result-attributes__tile"
        icon="lgbtqia-affirming-care-color"
        layout="row"
        align="center"
        :size="tileSize"
        type="body"
        :label="props.labels.lgbtqiaAffirmingCare"
      >
        <span slot="label" class="sp-finder-result-attributes__tile-label">
          {{ props.labels.lgbtqiaAffirmingCare }}
          <sp-tooltip>
            <sp-icon class="sp-finder-result-attributes__tooltip-activator" name="help-filled"></sp-icon>
            <div slot="content">{{ props.labels.lgbtqiaAffirmingCareTooltip }}</div>
          </sp-tooltip>
        </span>
      </sp-tile>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinder/i18n.js) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const props = defineProps({
  layout: {
    type: String,
    default: "directory",
    validator: (value) => ["bio", "directory"].includes(value),
  },
  result: {
    type: Object,
    default: () => ({}),
  },
  resultConfig: {
    type: Object,
    default: undefined,
  },
  labels: {
    type: Object,
    default: () => ({}),
  },
  resultType: {
    type: String,
    default: "provider",
    validator: (value) => ["provider", "location"].includes(value),
  },
  isDirectoryLayout: {
    type: Boolean,
    default: false,
  },
});

const isProvider = computed(() => props.resultType === "provider");
const isLocation = computed(() => props.resultType === "location");

const insurancePlanStatus = computed(() => props.result.insurancePlanStatus || {});

// Shown for both providers and locations (hospitals) — TDI requires an
// in-network status on every directory result. Legacy payloads can send the
// literal string "undefined" for status; results without insurance data omit
// the block entirely, so a missing status hides the badge.
const showInsurancePlanStatus = computed(
  () =>
    Boolean(insurancePlanStatus.value.status) &&
    insurancePlanStatus.value.status !== "self-pay" &&
    insurancePlanStatus.value.status !== "undefined",
);
const showPhoneNumber = computed(
  () => !props.isDirectoryLayout && props.result.phone_number && props.resultConfig?.showProviderPhoneNumber,
);
const showFaxNumber = computed(
  () => !props.isDirectoryLayout && props.result.fax_number && props.resultConfig?.showProviderFaxNumber,
);
const showAcceptingPatients = computed(
  () => props.result.accepting_patients && props.resultConfig?.showProviderAcceptingPatients,
);
// Tri-state: only show the negative badge when acceptance is explicitly false. A nil/undefined
// value (unknown — e.g. the source feed's "exempt" panel status, or an address the contracts feed
// never covered) shows neither badge rather than wrongly asserting "not accepting".
const showNotAcceptingPatients = computed(
  () => props.result.accepting_patients === false && props.resultConfig?.showProviderNotAcceptingPatients,
);
const tileSize = computed(() => (props.layout === "bio" ? "x-small" : "small"));

const showAgeGroup = computed(
  () =>
    props.resultConfig?.showProviderAgeGroup &&
    props.result.age_group_label &&
    (isLocation.value || !props.result.administrative_role),
);
const showProviderVisitType = computed(
  () => isProvider.value && props.resultConfig?.showProviderVisitType && props.result.provider_visit_type,
);
const showLocationVisitType = computed(
  () => isLocation.value && props.resultConfig?.showProviderVisitType && props.result.video_visits_available,
);
const showLgbtqiaAffirmingCare = computed(() => isProvider.value && props.result.lgbtqia_affirming_care);

const attributeTiles = computed(() =>
  [
    showPhoneNumber.value && {
      key: "phone-number",
      className: "sp-finder-result-attributes__tile--link",
      icon: "phone-filled",
      href: `tel:${props.result.phone_number}`,
      label: props.result.phone_number,
    },
    showFaxNumber.value && {
      key: "fax-number",
      className: "sp-finder-result-attributes__tile--link",
      icon: "fax-filled",
      href: `tel:${props.result.fax_number}`,
      label: props.result.fax_number,
    },
    showAcceptingPatients.value && {
      key: "accepting-patients",
      icon: "add-person-color",
      label: props.labels.acceptingPatients,
    },
    showNotAcceptingPatients.value && {
      key: "not-accepting-patients",
      icon: "returning-patient-color",
      label: props.labels.notAcceptingPatients,
    },
    showAgeGroup.value && {
      key: "age-group",
      icon: props.result.age_group_icon_name,
      label: props.result.age_group_label,
    },
    showProviderVisitType.value && {
      key: "provider-visit-type",
      icon: props.result.provider_visit_type_icon ? `${props.result.provider_visit_type_icon}-color` : undefined,
      label: props.result.provider_visit_type,
    },
    showLocationVisitType.value && {
      key: "location-visit-type",
      icon: "video-visit-filled",
      label: props.labels.videoVisitsAvailable,
    },
  ].filter(Boolean),
);

const hasAttributeList = computed(
  () => showInsurancePlanStatus.value || attributeTiles.value.length || showLgbtqiaAffirmingCare.value,
);
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
@use "@ce/components/SpFinder/styles/tile-row" as *;

.sp-finder-result-attributes {
  /* Layout */
  --attributes-list-gap: var(--sp-comp-finder-result-attributes-list-gap, var(--sp-ref-scale-4));
  --attributes-item-gap: var(--sp-comp-finder-result-attributes-item-gap, var(--sp-ref-scale-8));

  /* Color */
  --attributes-text-color: var(--sp-comp-finder-result-attributes-text-color, var(--sp-sys-color-on-surface));

  display: flex;
  flex-direction: column;
  color: var(--attributes-text-color);
  font: var(--sp-sys-typescale-body-short);

  &.--layout-bio {
    --attributes-bio-font: var(--sp-sys-typescale-body-font-weight) var(--sp-sys-typescale-body-font-size) / 1.5
      var(--sp-sys-typescale-body-font-family);
    --attributes-bio-link-font: var(--sp-sys-typescale-link-font-weight) var(--sp-sys-typescale-body-font-size) / 1.5
      var(--sp-sys-typescale-link-font-family);
    --attributes-list-gap: var(--sp-comp-finder-result-attributes-list-gap, 7px);
    --attributes-item-gap: var(--sp-comp-finder-result-attributes-item-gap, 5px);

    margin-bottom: var(--sp-comp-finder-result-attributes-margin-block-end, 7px);
    font: inherit;
  }

  &.--layout-bio &__tile {
    --sp-comp-tile-label-font-body: var(--attributes-bio-font);
    --sp-comp-tile-label-font-body-sm: var(--attributes-bio-font);
  }

  &.--layout-bio &__tile--link {
    --sp-comp-tile-label-font-body: var(--attributes-bio-link-font);
    --sp-comp-tile-label-font-body-sm: var(--attributes-bio-link-font);
    --sp-comp-tile-label-color: var(--sp-comp-finder-result-attributes-link-color, var(--sp-sys-color-link));
    --sp-comp-tile-label-text-decoration-hover: none;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: var(--attributes-list-gap);
  }

  &__detail {
    display: flex;
    gap: var(--attributes-item-gap);
    align-items: flex-start;
    margin: 0;
  }

  &__tile {
    @include finder-tile-row;

    --sp-comp-tile-gap: var(--attributes-item-gap);
    --sp-comp-tile-gap-sm: var(--attributes-item-gap);
    --sp-comp-tile-label-color: var(--attributes-text-color);
    --sp-comp-tile-label-color-hover: var(--sp-comp-tile-label-color);

    &:not(.sp-finder-result-attributes__tile--link) {
      --sp-comp-tile-label-text-decoration: none;
    }

    &--link {
      --sp-comp-tile-inline-size: fit-content;
      --sp-comp-tile-max-inline-size: 100%;
      --sp-comp-tile-label-text-decoration: var(--sp-comp-finder-result-attributes-link-text-decoration, none);
      --sp-comp-tile-label-text-decoration-hover: underline;
    }
  }

  &__insurance-app {
    --sp-comp-insurance-app-status-icon-size: var(--sp-sys-icon-size-small);
    --sp-comp-insurance-app-tiny-column-gap: var(--attributes-item-gap);
  }

  &__tile-label {
    display: inline-flex;
    align-items: center;
  }

  &__tooltip-activator {
    --size: var(--sp-sys-icon-size-small);
    margin-inline-start: var(--sp-ref-scale-4);
  }
}
</style>
